/**
 * Кто решает, на каком языке говорит кабинет.
 *
 * Три уровня, каждый уже предыдущего:
 *   1. ДОМЕН задаёт НАБОР (`GET /api/v1/cabinet/site` по хосту запроса);
 *   2. ПРОФИЛЬ (`users.ui_lang`) задаёт выбор внутри набора;
 *   3. до входа выбор живёт в localStorage, потом переносится в профиль.
 *
 * Домен главнее профиля: профиль `ru` на домене, где разрешён только `en`, даёт
 * английский кабинет — и профиль при этом НЕ переписывается, чтобы возврат на
 * `rybprognoz.ru` вернул русский.
 *
 * Неизвестный домен → русский. Иначе новый домен, ещё не заведённый в админке,
 * ронял бы кабинет до первой записи в базе.
 */
import type { DomainFrame, DomainPeriod } from '../domain/limits'
import { isLang, type Lang } from './types'

export const FALLBACK_LANG: Lang = 'ru'

/**
 * Бренд домена — айдентика пака `branding/<id>/` как ДАННЫЕ.
 *
 * Имя бренда едет отсюда, а не из каталога переводов: перевести можно слово, а
 * имя партнёра — это запись в его манифесте. Ключ `brand.name` остаётся
 * ЗАПАСНЫМ — на случай, когда пак не доехал.
 *
 * Но данные знают язык. Домен бывает двуязычным (`rybprognoz.ru` — ru + en), и
 * одно имя на весь домен показывало бы «Румб» кириллицей в английском кабинете
 * — ровно та регрессия, ради которой заведён `names`.
 */
export interface SiteBrand {
  id: string
  /** Имя без уточнения языка: запасное для языка, которого нет в `names`. */
  name: string
  /** Имя по языкам кабинета (`{en: 'Rumb'}`); пусто — имя одно на все языки. */
  names: Readonly<Record<string, string>>
  /** `url` — путь на нашем же origin; `mono` — знак красится currentColor. */
  logo: { url: string; mono: boolean }
  regions: readonly string[]
  /** Код палитры тайлов домена; `null` — домен красит нашей гаммой, и адрес
   *  тайла в этом случае в точности прежний. */
  palette_code?: string | null
  /** Цвета линий карты: изотермы, фронты, снос, маркеры. Каналы, а не строки:
   *  MapLibre разбирает цвета сам, CSS-переменной ему не подать. Ключ `drift` —
   *  шкала, остальные один цвет. Пусто — домен рисует нашей гаммой. */
  map?: Readonly<Record<string, number[] | number[][]>>
  /** Подложка карты домена: поставщик и его ключ. `null` — подложка прежняя
   *  (Carto без ключа), то есть ровно то, что было до 27.08.2026.
   *
   *  Ключ приходит СЮДА, а не лежит в паке: пак версионируется в репозитории,
   *  ключ ротируется. Секретом он при этом не является по устройству — едет в
   *  адресе каждого тайла и виден в браузере. */
  basemap?: Readonly<{ provider: string; apikey?: string }> | null
  /** Флаг «нацфлота» домена, ISO alpha-3 (`OMN`); `null` — прежний (РФ). */
  national_flag?: string | null
  /** Вид, на котором кабинет открывается без вида в ссылке; `null` — прежние
   *  умолчания разделов. */
  default_species?: number | null
  /** Рамка домена (решение владельца 20.10.2026); `null` — вся вода районов. */
  frame?: DomainFrame | null
  /** Окно дат домена; `null` — прежние −7…+2. */
  period?: DomainPeriod | null
}

/** Ответ `GET /api/v1/cabinet/site`: что домен разрешает на этом хосте. */
export interface SiteLangs {
  languages: readonly string[]
  default_lang: string
  /**
   * Бренд домена. `null` — ручка не ответила, ответила в старом формате или
   * запомненное значение повреждено: тогда имя берётся из каталога, а знак
   * рисуется встроенный. Поле необязательное, потому что набор языков и бренд
   * приезжают одним ответом, но живут порознь: потеря бренда не имеет права
   * отнимать у витрины набор языков (иначе вернётся вспышка русского кадра,
   * ради которой заведён siteCache).
   */
  brand?: SiteBrand | null
}

export interface LangInputs {
  /** null — хост неизвестен либо ручка не ответила. */
  site: SiteLangs | null
  profileLang: string | null
  storedLang: string | null
  /** `navigator.languages` как есть (`['en-GB','ru']`). */
  browserLangs: readonly string[]
}

/** Набор языков домена, отфильтрованный до тех, что кабинет вообще знает. */
export function siteLangs(site: SiteLangs | null): Lang[] {
  if (!site) return []
  return site.languages.filter(isLang)
}

/** Первый язык браузера, входящий в набор (сравнение по префиксу тега). */
export function browserLang(browserLangs: readonly string[], allowed: readonly Lang[]): Lang | null {
  for (const tag of browserLangs) {
    const base = tag.toLowerCase().split('-')[0]
    if (isLang(base) && allowed.includes(base)) return base
  }
  return null
}

/** Показывать ли переключатель: только когда на домене есть из чего выбирать. */
export function showSwitcher(site: SiteLangs | null): boolean {
  return siteLangs(site).length > 1
}

export function resolveLang(inputs: LangInputs): Lang {
  const allowed = siteLangs(inputs.site)
  if (!allowed.length) return FALLBACK_LANG
  if (allowed.length === 1) return allowed[0]

  const wanted = [inputs.profileLang, inputs.storedLang]
  for (const candidate of wanted) {
    if (isLang(candidate) && allowed.includes(candidate)) return candidate
  }
  const fromBrowser = browserLang(inputs.browserLangs, allowed)
  if (fromBrowser) return fromBrowser

  const fallback = inputs.site?.default_lang
  if (isLang(fallback) && allowed.includes(fallback)) return fallback
  return allowed[0]
}
